import { Button, Chip, Divider, Link, Modal, ModalBody, ModalContent, Snippet } from "@heroui/react";
import { CheckIcon } from "lucide-react";
import React from "react";
import type { Address } from "viem";
import { useL1TokenRegMutation, useL2TokenRegMutation } from "../../mutations/tokenReg";
import { type TokenDetails, TokenProposalStatus, useTokenDetails } from "../../queries/tokenReg";
import { useBlockWaitingProgress } from "../../state/blockWaitingProgress";
import { useTokenRegTxHash } from "../../state/tokenRegTxHash";
import { l1BlockExplorerUrl, l2BlockExplorerUrl, shortHex } from "../../utils";
import { BlockWaitingProgress } from "../BlockWaitingProgress";
import { CheckCircle, NumberRing } from "../Step";

export function TokenRegDetailsModal({
  isOpen,
  onClose,
  tokenData,
}: {
  isOpen: boolean;
  onClose: () => void;
  tokenData: TokenDetails | null;
}) {
  // Don't render the modal content if not open or no token data
  // This prevents hooks from being called unnecessarily
  if (!isOpen || !tokenData) {
    return null;
  }

  return <TokenRegDetailsModalContent tokenData={tokenData} isOpen={isOpen} onClose={onClose} />;
}

function TokenRegDetailsModalContent({
  isOpen,
  onClose,
  tokenData,
}: {
  isOpen: boolean;
  onClose: () => void;
  tokenData: TokenDetails;
}) {
  // Now we know we have valid tokenData, so we can safely call the hook
  const { data: currentTokenData } = useTokenDetails(tokenData.tokenAddress);

  // Use current data if available, otherwise fall back to prop data
  const displayData = currentTokenData || tokenData;

  // Close modal when token is fully registered
  const handleSuccess = React.useCallback(() => {
    // Trust the optimistic update - check the current cached data
    if (displayData.l1RegistrationStatus && displayData.l2RegistrationStatus) {
      onClose();
    }
  }, [onClose, displayData.l1RegistrationStatus, displayData.l2RegistrationStatus]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      classNames={{
        base: "turnstile-modal-border rounded-3xl",
        closeButton: "text-white text-xl top-4 right-5",
        header: "pt-5 pb-3",
        body: "items-center p-6 gap-4",
        backdrop: "turnstile-modal-backdrop",
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="flex flex-col items-center gap-2 my-4">
            <div className="rounded-full size-16 bg-violet-500" />
            <div className="font-semibold">
              {displayData.tokenName} ({displayData.tokenSymbol})
            </div>
            <Snippet
              size="sm"
              hideSymbol
              codeString={displayData.tokenAddress}
              classNames={{
                pre: "text-xs",
                copyButton: "text-sm !min-w-4 !size-4",
              }}
            >
              {displayData.tokenAddress}
            </Snippet>
          </div>

          <Divider />

          <TokenRegSteps tokenData={displayData} onSuccess={handleSuccess} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function TokenRegSteps({ tokenData, onSuccess }: { tokenData: TokenDetails | null; onSuccess: () => void }) {
  if (!tokenData) return null;

  if (tokenData.l1RegistrationStatus && tokenData.l2RegistrationStatus) {
    return <FullyRegisteredState tokenAddress={tokenData.tokenAddress} />;
  }

  if (tokenData.l1RegistrationStatus) {
    return <L1RegisteredState tokenAddress={tokenData.tokenAddress} onSuccess={onSuccess} />;
  }

  switch (tokenData.l1ProposalStatus) {
    case TokenProposalStatus.PROPOSED:
      return <ProposedState />;
    case TokenProposalStatus.ACCEPTED:
      return <AcceptedState tokenAddress={tokenData.tokenAddress} onSuccess={onSuccess} />;
    default:
      return null;
  }
}

function ProposedState() {
  return (
    <div className="w-full mt-4">
      <div className="flex items-center gap-4">
        <NumberRing digit={1} isSelected={true} />
        <div className="flex-1">
          <div className="font-[500]">Proposal</div>
          <div className="text-[#ADC0D0] text-xs absolute max-w-72">
            Token proposal is currently being reviewed. The process can take up to 3 days.
          </div>
        </div>
        <Chip
          variant="flat"
          radius="sm"
          size="sm"
          className="bg-[#F6851B]/20 text-[#F6851B]"
          classNames={{
            content: "font-semibold",
          }}
        >
          In Progress
        </Chip>
      </div>
      <div className="mx-4 my-2 h-12 border-l-2 border-dashed" />
      <div className="flex items-center gap-4">
        <NumberRing digit={2} isSelected={false} />
        <div className="flex-1">
          <div className="font-[500] text-[#ADC0D0]">L1 Registration</div>
        </div>
      </div>
      <div className="mx-4 my-2 h-6 border-l-2 border-dashed" />
      <div className="flex items-center gap-4">
        <NumberRing digit={3} isSelected={false} />
        <div className="flex-1">
          <div className="font-[500] text-[#ADC0D0]">L2 Registration</div>
        </div>
      </div>
    </div>
  );
}

function AcceptedState({ tokenAddress, onSuccess }: { tokenAddress: Address; onSuccess: () => void }) {
  const l1RegMutation = useL1TokenRegMutation();

  // Call onSuccess when L1 registration completes
  React.useEffect(() => {
    if (l1RegMutation.isSuccess) {
      onSuccess();
    }
  }, [l1RegMutation.isSuccess, onSuccess]);

  return (
    <>
      <div className="w-full mt-4">
        <div className="flex items-center gap-4">
          <CheckCircle />
          <div className="flex-1">
            <div className="font-[500] text-[#ADC0D0]">Proposal</div>
          </div>
          <Chip
            variant="flat"
            radius="sm"
            size="sm"
            className="bg-[#36CC36]/20 text-[#36CC36]"
            classNames={{
              content: "font-semibold",
            }}
            startContent={<CheckIcon className="size-4" />}
          >
            Accepted
          </Chip>
        </div>
        <div className="mx-4 my-2 h-6 border-l-2 border-dashed border-[#14DBC0]" />
        <div className="flex items-center gap-4">
          <NumberRing digit={2} isSelected={true} />
          <div className="flex-1">
            <div className="font-[500]">L1 Registration</div>
            <div className="text-[#ADC0D0] text-xs absolute max-w-72">
              Execute and confirm wallet transaction to register token on Layer 1.{" "}
            </div>
          </div>
          <Chip
            variant="flat"
            radius="sm"
            size="sm"
            className="bg-[#F6851B]/20 text-[#F6851B]"
            classNames={{
              content: "font-semibold",
            }}
          >
            Action required
          </Chip>
        </div>
        <div className="mx-4 my-2 h-12 border-l-2 border-dashed" />
        <div className="flex items-center gap-4">
          <NumberRing digit={3} isSelected={false} />
          <div className="flex-1">
            <div className="font-[500] text-[#ADC0D0]">L2 Registration</div>
          </div>
        </div>
      </div>

      {l1RegMutation.status === "pending" ? (
        <Button fullWidth radius="full" size="lg" className="font-semibold text-large bg-[#3F6784]" isLoading>
          Confirm in the wallet...
        </Button>
      ) : (
        <Button
          fullWidth
          radius="full"
          size="lg"
          color="primary"
          className="font-semibold text-large"
          onPress={() => l1RegMutation.mutate({ tokenAddress })}
        >
          Register on L1
        </Button>
      )}
    </>
  );
}

function L1RegisteredState({ tokenAddress, onSuccess }: { tokenAddress: Address; onSuccess: () => void }) {
  const l2RegMutation = useL2TokenRegMutation();
  const blockWaitingProgress = useBlockWaitingProgress();

  // Call onSuccess when L2 registration completes
  React.useEffect(() => {
    if (l2RegMutation.isSuccess) {
      // Trust the optimistic update - no need to refetch
      onSuccess();
    }
  }, [l2RegMutation.isSuccess, onSuccess]);

  return (
    <>
      <div className="w-full mt-4">
        <div className="flex items-center gap-4">
          <CheckCircle />
          <div className="flex-1">
            <div className="font-[500] text-[#ADC0D0]">Proposal</div>
          </div>
          <Chip
            variant="flat"
            radius="sm"
            size="sm"
            className="bg-[#36CC36]/20 text-[#36CC36]"
            classNames={{
              content: "font-semibold",
            }}
            startContent={<CheckIcon className="size-4" />}
          >
            Accepted
          </Chip>
        </div>
        <div className="mx-4 my-2 h-6 border-l-2 border-dashed border-[#14DBC0]" />
        <div className="flex items-center gap-4">
          <CheckCircle />
          <div className="flex-1">
            <div className="font-[500] text-[#ADC0D0]">L1 Registration</div>
          </div>
          <Chip
            variant="flat"
            radius="sm"
            size="sm"
            className="bg-[#36CC36]/20 text-[#36CC36]"
            classNames={{
              content: "font-semibold",
            }}
            startContent={<CheckIcon className="size-4" />}
          >
            Registered
          </Chip>
        </div>
        <div className="mx-4 my-2 h-6 border-l-2 border-dashed border-[#14DBC0]" />
        <div className="flex items-center gap-4 mb-8">
          <NumberRing digit={3} isSelected={true} />
          <div className="flex-1">
            <div className="font-[500]">L2 Registration</div>
            <div className="text-[#ADC0D0] text-xs absolute max-w-72">
              Execute and confirm wallet transaction to register token on Layer 2.
            </div>
          </div>
          <Chip
            variant="flat"
            radius="sm"
            size="sm"
            className="bg-[#F6851B]/20 text-[#F6851B]"
            classNames={{
              content: "font-semibold",
            }}
          >
            Action required
          </Chip>
        </div>
      </div>

      {blockWaitingProgress && blockWaitingProgress.isWaiting && (
        <div className="mb-4">
          <BlockWaitingProgress
            currentBlock={blockWaitingProgress.currentBlock}
            targetBlock={blockWaitingProgress.targetBlock}
            label="Waiting for L1→L2 message to be available"
          />
        </div>
      )}

      {l2RegMutation.status === "pending" ? (
        <Button fullWidth radius="full" size="lg" className="font-semibold text-large bg-[#3F6784]" isLoading>
          {blockWaitingProgress && blockWaitingProgress.isWaiting
            ? "Waiting for L2 block..."
            : "Confirm in the wallet..."}
        </Button>
      ) : (
        <Button
          fullWidth
          radius="full"
          size="lg"
          color="primary"
          className="font-semibold text-large"
          onPress={() => l2RegMutation.mutate({ tokenAddress })}
        >
          Register on L2
        </Button>
      )}
    </>
  );
}

function FullyRegisteredState({ tokenAddress }: { tokenAddress: Address }) {
  const tokenRegTxHash = useTokenRegTxHash(tokenAddress);

  return (
    <div className="w-full mt-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[#ADC0D0] font-semibold text-sm">Status</div>
        <Chip
          variant="flat"
          radius="sm"
          size="sm"
          className="bg-[#36CC36]/20 text-[#36CC36]"
          classNames={{
            content: "font-semibold",
          }}
          startContent={<CheckIcon className="size-4" />}
        >
          Registered
        </Chip>
      </div>
      {tokenRegTxHash && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-[#ADC0D0] font-semibold text-sm">Proposed on L1</div>
            <div className="font-semibold">
              <Link
                color="foreground"
                href={l1BlockExplorerUrl(tokenRegTxHash.proposedTxHash ?? "")}
                isExternal
                showAnchorIcon
              >
                {shortHex(tokenRegTxHash.proposedTxHash ?? "")}
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[#ADC0D0] font-semibold text-sm">Registered on L1</div>
            <div className="font-semibold">
              <Link
                color="foreground"
                href={l1BlockExplorerUrl(tokenRegTxHash.l1RegisteredTxHash ?? "")}
                isExternal
                showAnchorIcon
              >
                {shortHex(tokenRegTxHash.l1RegisteredTxHash ?? "")}
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[#ADC0D0] font-semibold text-sm">Registered on L2</div>
            <div className="font-semibold">
              <Link
                color="foreground"
                href={l2BlockExplorerUrl(tokenRegTxHash.l2RegisteredTxHash ?? "")}
                isExternal
                showAnchorIcon
              >
                {shortHex(tokenRegTxHash.l2RegisteredTxHash ?? "")}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
